import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Link, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import DispatchService from '../../setting/dispatch/DispatchService';
import OrderByOptionService from '../../setting/orderByOption/OrderByOptionService';
import { REASONS, MODE } from './dispatchReasons';

// Every order the bot took, where it stands right now (owner 2026-10-31: during the pilot the
// admin must be able to see each job, not only the ones that became tasks). The tasks are where
// things get done; the one action here is "ส่งการ์ดให้…" on a job that has not ended — the
// board's force send, so a card that LINE never delivered (or went to the wrong person) can go
// to the buyer the admin wants (owner 2026-11-01). A button on the Mercari Admin page opens it
// as a popup (owner 2026-10-31); the no-confirm-image page opens the same popup with the
// "ยังไม่จบ" filter preset (open / onClose / defaultStatus props). It loads and refreshes only
// while open. Same shop scope as the page.

const POLL_MS = 60 * 1000;

const STATUS = {
  pending: { label: 'รอบอทส่งการ์ด', color: 'default' },
  offered: { label: 'กำลังส่งให้คนกด', color: 'info' },
  needs_admin: { label: 'รอแอดมิน', color: 'warning' },
  waiting_proof: { label: 'ขายแล้ว รอหลักฐาน', color: 'info' },
  deferred: { label: 'รอส่งใหม่ 07:00', color: 'info' },
  done: { label: 'กดสำเร็จ', color: 'success' },
  sold_out: { label: 'ซื้อไม่ทัน', color: 'error' },
  admin_taken: { label: 'แอดมินซื้อเอง', color: 'default' },
  cancelled: { label: 'ยกเลิก', color: 'default' },
};
const HOURS = [{ v: 24, label: '24 ชั่วโมง' }, { v: 72, label: '3 วัน' }, { v: 168, label: '7 วัน' }];
const FINAL = ['done', 'sold_out', 'admin_taken', 'cancelled'];
const LIVE = 'live'; // status filter "ยังไม่จบ": every job that is not final (filtered here — the API takes one status)
const FORCE_CONFIRM = 'ส่งการ์ดให้คนนี้ทันที ไม่สนเวลา/เรือแอร์/ร้าน — งานนี้จะเป็นของเขา ไม่ส่งต่อใคร';

const minutesSince = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : null);
const shortLink = (url) => (url || '').replace(/^https?:\/\/(www\.)?/, '').slice(0, 40);

/** One line per card: who, and what they did with it. */
const cardLine = (o) => {
  const who = o.orderer?.label || o.orderByOptionId;
  if (o.status === 'assigned') return `${who} (งานฝั่งแอดมิน)`;
  const marks = [];
  if (o.status === 'done') marks.push('ส่งหลักฐานแล้ว ✓');
  else {
    if (o.openedAt) marks.push('เปิดลิงก์');
    if (o.proofOpenedAt) marks.push('เปิดหน้าหลักฐาน');
    if (o.declinedAt) marks.push('กดไม่ได้');
    if (o.status === 'send_failed') marks.push('ส่งการ์ดไม่สำเร็จ');
    if (marks.length === 0) marks.push(o.status === 'offered' || o.status === 'engaged' ? 'ยังไม่ตอบ' : 'ไม่ตอบ');
  }
  return `${who}: ${marks.join(' · ')}`;
};

/** The buyer's sheet: did the row land, and if not, why (only for jobs that ended with a proof). */
const sheetCell = (job) => {
  if (job.status !== 'done') return <Typography variant="caption" color="text.secondary">—</Typography>;
  const row = job.order?.googleSheetPushedRow;
  if (job.order?.googleSheetPushedAt) return <Typography variant="caption" color="success.main">✓ แถว {row}</Typography>;
  if (job.sheetError) return <Typography variant="caption" color="error.main">{job.sheetError}</Typography>;
  return <Typography variant="caption" color="text.secondary">รอส่ง (ลองใหม่ทุกชั่วโมง)</Typography>;
};

/** Where the job is right now, in one phrase. */
const nowAt = (job) => {
  if (job.status === 'done') {
    const winner = (job.offers || []).find((o) => o.status === 'done')?.orderer?.label;
    return winner ? `กดโดย ${winner}` : 'กดสำเร็จ';
  }
  if (job.status === 'pending' && !job.currentOfferId) return 'ยังไม่ได้ส่งการ์ด';
  if (job.status === 'needs_admin') return REASONS[job.adminReason]?.label || job.adminReason || 'รอแอดมิน';
  if (job.status === 'deferred') return 'เลื่อนไปส่งตอนเช้า 07:00';
  const cur = job.currentOffer;
  if (cur?.orderer?.label) {
    const m = minutesSince(cur.sentAt);
    return `${cur.orderer.label}${m != null ? ` (${m} นาที)` : ''}`;
  }
  return '—';
};

export default function DispatchJobsSection({ open: openProp, onClose, defaultStatus = '' } = {}) {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [hours, setHours] = useState(24);
  const [status, setStatus] = useState(defaultStatus);
  const [buyers, setBuyers] = useState([]);
  const [pick, setPick] = useState({}); // jobId → buyer chosen in that row
  const [busyId, setBusyId] = useState(null);
  const shopName = useSelector((state) => state.shop.name);
  // Controlled by a parent (no-confirm-image page) or by its own button (Mercari Admin page).
  const controlled = openProp !== undefined;
  const isOpen = controlled ? openProp : open;
  const close = () => (controlled ? onClose?.() : setOpen(false));

  const load = useCallback(async () => {
    try {
      const res = await DispatchService.getJobs({ status: status && status !== LIVE ? status : null, shop: shopName || null, hours });
      const list = res.data.jobs || [];
      setJobs(status === LIVE ? list.filter((j) => !FINAL.includes(j.status)) : list);
    } catch (err) {
      // transient failure keeps the last list
    }
  }, [status, shopName, hours]);

  useEffect(() => {
    if (!isOpen) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, { withCredentials: true });
    socket.on('case_claim_updated', ({ caseType } = {}) => { if (caseType === 'dispatch') load(); });
    return () => { clearInterval(id); socket.disconnect(); };
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen || buyers.length > 0) return;
    OrderByOptionService.getActiveOrderByOptions().then((res) => setBuyers(res.data || [])).catch(() => {});
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same action and words as the task modal's "ส่งการ์ดให้คนอื่น": the job becomes that buyer's.
  const resend = async (job) => {
    const buyerId = Number(pick[job.id]);
    if (!buyerId || !window.confirm(FORCE_CONFIRM)) return;
    setBusyId(job.id);
    try {
      await DispatchService.forceSend(job.id, buyerId);
      toast.success('ส่งการ์ดแล้ว — งานนี้เป็นของคนกดคนนั้น');
      setPick((p) => ({ ...p, [job.id]: '' }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ส่งการ์ดไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  const dialog = (
      <Dialog open={isOpen} onClose={close} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        ออเดอร์ที่ส่งให้บอทกด
        <Chip size="small" label={jobs.length} />
        <Box flexGrow={1} />
        <IconButton size="small" onClick={close}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
      <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>ช่วงเวลา</InputLabel>
          <Select value={hours} label="ช่วงเวลา" onChange={(e) => setHours(Number(e.target.value))}>
            {HOURS.map((h) => <MenuItem key={h.v} value={h.v}>{h.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>สถานะ</InputLabel>
          <Select value={status} label="สถานะ" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">ทั้งหมด</MenuItem>
            <MenuItem value={LIVE}>ยังไม่จบ</MenuItem>
            {Object.entries(STATUS).map(([k, s]) => <MenuItem key={k} value={k}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>คีย์เมื่อ</TableCell>
              <TableCell>ลูกค้า</TableCell>
              <TableCell>สินค้า</TableCell>
              <TableCell>เรือ/แอร์</TableCell>
              <TableCell>สถานะ</TableCell>
              <TableCell>ตอนนี้</TableCell>
              <TableCell>การ์ดที่ส่ง</TableCell>
              <TableCell>ชีทคนกด</TableCell>
              <TableCell>ส่งการ์ดให้</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">ไม่มีออเดอร์ในช่วงนี้</Typography></TableCell></TableRow>
            )}
            {jobs.map((job) => {
              // A site purchase request (plan E) has no order until the proof: read the subject.
              const sub = job.subject || {};
              const order = job.order || { id: null, url: sub.url, contact: sub.contact, smileNameInput: sub.customer };
              const who = order.contact || order.smileNameInput || (sub.kind === 'purchase' ? `คำขอ #${sub.id}` : `#${order.id}`);
              const s = STATUS[job.status] || { label: job.status, color: 'default' };
              const cards = (job.offers || []).filter((o) => o.kind !== 'admin');
              return (
                <TableRow key={job.id} hover>
                  <TableCell>{job.createdAt ? format(new Date(job.createdAt), 'd MMM HH:mm') : '—'}</TableCell>
                  <TableCell>{who}{sub.kind === 'purchase' && !job.order ? <Chip size="small" variant="outlined" label="เว็บ" sx={{ ml: 0.5 }} /> : null}</TableCell>
                  <TableCell>
                    <Link href={order.url} target="_blank" rel="noreferrer" underline="hover">{shortLink(order.url)}</Link>
                    {job.shopItem && <Chip size="small" variant="outlined" color="warning" label="Shops" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>{MODE[job.mode] || job.mode}</TableCell>
                  <TableCell><Chip size="small" color={s.color} label={s.label} /></TableCell>
                  <TableCell>{nowAt(job)}</TableCell>
                  <TableCell>
                    {cards.length === 0
                      ? <Typography variant="caption" color="text.secondary">ยังไม่มี</Typography>
                      : cards.map((o) => <Typography key={o.id} variant="caption" display="block">{cardLine(o)}</Typography>)}
                  </TableCell>
                  <TableCell>{sheetCell(job)}</TableCell>
                  <TableCell>
                    {FINAL.includes(job.status) ? <Typography variant="caption" color="text.secondary">—</Typography> : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Select
                          size="small"
                          displayEmpty
                          value={pick[job.id] || ''}
                          onChange={(e) => setPick((p) => ({ ...p, [job.id]: e.target.value }))}
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value=""><em>เลือกคนกด</em></MenuItem>
                          {buyers.filter((b) => b.dispatchLineId || b.lineGroupId).map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.label}</MenuItem>)}
                        </Select>
                        <Button size="small" variant="outlined" disabled={!pick[job.id] || busyId === job.id} onClick={() => resend(job)}>ส่งเลย</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </DialogContent>
      </Dialog>
  );
  if (controlled) return dialog;
  return (
    <Box mt={2}>
      <Button variant="outlined" size="small" onClick={() => setOpen(true)}>ออเดอร์ที่ส่งให้บอทกด</Button>
      {dialog}
    </Box>
  );
}
